import React from 'react'
import cn from 'classnames'
import * as styles from './TwoUpGrid.module.scss'
import TitleLockup from 'src/modules/molecules/title-lockup'
import WistiaEmbed from 'src/modules/molecules/wistia-embed'

type TwoUpGridProps = {
  id?: string
  reverse?: boolean
  contentBg?: 'white' | 'gray' | 'black'
  padding?:
    | 'none'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | '7xl'
    | '8xl'
  bgColor?: 'white' | 'gray' | 'black'
  mediaType?: 'image' | 'video'
  mediaSrc?: string
  altText?: string
  sideContent?: React.ReactNode | string
  sideContentBgSrc?: string
  title?: string
  titleAlt?: string
  titleHeadingLevel?: 'h2' | 'h3' | 'h4' | 'h5'
  titleVisualStyle?: 'h2' | 'h3' | 'h4' | 'h5'
  titleTextWrap?: boolean | 'desktopOnly' | 'mobileOnly'
  content?: React.ReactNode | string
  wistiaAriaLabel?: string
  wistiaAspect?: string
  layout?: 'block' | 'simple'
  button?: React.ReactNode
}

const getPaddingValue = (padding?: string) => {
  switch (padding) {
    case 'none':
      return '0'
    case 'sm':
      return 'var(--space-sm)'
    case 'md':
      return 'var(--space-md)'
    case 'base':
      return 'var(--space-base)'
    case 'lg':
      return 'var(--space-lg)'
    case 'xl':
      return 'var(--space-xl)'
    case '2xl':
      return 'var(--space-2xl)'
    case '3xl':
      return 'var(--space-3xl)'
    case '4xl':
      return 'var(--space-4xl)'
    case '5xl':
      return 'var(--space-5xl)'
    case '6xl':
      return 'var(--space-6xl)'
    case '7xl':
      return 'var(--space-7xl)'
    case '8xl':
      return 'var(--space-8xl)'
    default:
      return 'var(--space-md)'
  }
}

const getBackgroundColor = (bgColor?: string) => {
  switch (bgColor) {
    case 'gray':
      return 'var(--color-gray-50)'
    case 'dark':
      return 'var(--color-dark)'
    case 'light':
    default:
      return 'var(--color-light)'
  }
}

const TwoUpGrid: React.FC<TwoUpGridProps> = ({
  id,
  reverse = false,
  contentBg = 'gray',
  padding = 'md',
  bgColor = 'light',
  mediaType = 'image',
  mediaSrc,
  altText = '',
  sideContent,
  sideContentBgSrc,
  title,
  titleAlt,
  titleHeadingLevel,
  titleVisualStyle,
  titleTextWrap,
  content,
  wistiaAriaLabel,
  wistiaAspect = '1',
  layout = 'block',
  button
}) => {
  const renderNode = (node: React.ReactNode | string) => {
    if (!node) return null
    if (typeof node === 'string') {
      return <div dangerouslySetInnerHTML={{ __html: node }} />
    }
    return <>{node}</>
  }

  const sideContentStyle = sideContentBgSrc
    ? { backgroundImage: `url(${sideContentBgSrc})`, backgroundSize: 'cover' }
    : { backgroundColor: getBackgroundColor(contentBg) }

  return (
    <section
      id={id}
      className={cn(styles.wrapper, styles[layout])}
      style={
        {
          paddingBlock: 'var(--space-base)', // Default for mobile
          backgroundColor: getBackgroundColor(bgColor),
          '--dynamic-padding-block': getPaddingValue(padding) // Custom property for tablet and up
        } as React.CSSProperties
      }
    >
      <div className={cn(styles.container, { [styles.reverse]: reverse })}>
        {/* LEFT SIDE */}
        {mediaSrc ? (
          mediaType === 'video' ? (
            <div className={styles.containerVideo}>
              <WistiaEmbed
                mediaId={mediaSrc}
                aspect={wistiaAspect}
                ariaLabel={wistiaAriaLabel || 'Video content'}
              />
            </div>
          ) : (
            <div className={styles.containerImg}>
              <img src={mediaSrc} alt={altText} />
            </div>
          )
        ) : (
          <div
            className={styles.containerContentSide}
            style={{ ...sideContentStyle }}
          >
            {renderNode(sideContent)}
          </div>
        )}

        {/* RIGHT SIDE */}
        <div
          className={styles.containerContent}
          style={{ backgroundColor: getBackgroundColor(contentBg) }}
        >
          <TitleLockup
            headingLevel={titleHeadingLevel || 'h3'}
            visualStyle={titleVisualStyle || 'h3'}
            title={title}
            titleAlt={titleAlt}
            align='left'
            textWrap={titleTextWrap}
          />
          <div>
            {renderNode(content)}
            {button && <div className={styles.buttonContainer}>{button}</div>}
          </div>
        </div>
      </div>
    </section>
  )
}

export default TwoUpGrid
